import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input, Spinner } from 'reactstrap';
import cs_analysis_fetch from '../../../utils/cs_analysis_fetch';

/**
 * Escalation alerts for one client (cs-analysis /api/v1/alerts) with rep
 * feedback. The per-message alerter (cs-analysis docs/design-escalation-alerts.md)
 * runs keyword recall → staff-sender suppression → flash intent judge and
 * persists EVERY hit with a machine verdict. The rep's ✓ Genuine / ✗ Not an
 * escalation on each row is the labelled set the judge is tuned against;
 * the endpoint is idempotent and last-verdict-wins, so a chip can be changed.
 * Suppressed hits are fetched only on demand (includeSuppressed=1) so the
 * default view is what actually reached the GChat space / rep DM.
 *
 * `store_id` is the same id Findings / ClientComms hand to cs-analysis as
 * the client id (/api/v1/clients/:store_id/…).
 */

const fmt_ts = (d) => { try { return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch (e) { return ''; } };
const cut_text = (s, n) => { const t = String(s || '').replace(/\s+/g, ' ').trim(); return t.length > n ? t.slice(0, n - 1) + '…' : t; };

// Mirrors Findings.jsx SEVERITY_COLOR for the three alert severities.
const SEVERITY_COLOR = { HIGH: '#e67e22', MEDIUM: '#f1c40f', LOW: '#3498db' };

/** Machine verdicts the default listing hides (server: includeSuppressed=0). */
const SUPPRESSED_VERDICTS = { SUPPRESSED_NOT_ESCALATION: true, SUPPRESSED_STAFF: true, JUDGE_FAILED: true };
export const is_suppressed = (a) => !!(a && SUPPRESSED_VERDICTS[a.verdict]);

const VERDICT_LABEL = {
    FIRED: 'fired',
    SUPPRESSED_NOT_ESCALATION: 'suppressed · judge: not an escalation',
    SUPPRESSED_STAFF: 'suppressed · staff sender',
    JUDGE_FAILED: 'judge failed',
    REP_REPORTED: 'reported by a rep as missed',
};
export const verdict_label = (verdict) => VERDICT_LABEL[verdict] || verdict || '';

/** Deep link into the Chat inbox at the alerting message — Chat/index.jsx
 *  reads ?channel=&seq= (same link web-push clicks use). null when the alert
 *  carries no message pointer. */
export const alert_chat_href = (a) => {
    if (!a || !a.channelId || a.seq === null || a.seq === undefined || a.seq === '') return null;
    return `/home/chat?channel=${encodeURIComponent(a.channelId)}&seq=${encodeURIComponent(a.seq)}`;
};

/** A client-side (inbound) message — the only kind an escalation can be
 *  reported on. Staff, system and AI-authored messages are never inbound,
 *  so a whitelist keeps the action off them whatever authorType they carry. */
export const is_client_inbound = (m) => !!m && !m.deletedAt && (m.authorType === 'CLIENT_USER' || m.authorType === 'GUEST');

/** Fold a feedback response into the list. Prefers the server's row when it
 *  echoes one ({alert} or the bare alert); otherwise patches optimistically —
 *  the endpoint is idempotent and last-verdict-wins, so the patch is exact. */
export const apply_feedback = (alerts, id, verdict, note, resp, now) => {
    const echoed = resp && resp.alert && resp.alert.id === id ? resp.alert
        : resp && resp.id === id && resp.repVerdict ? resp : null;
    return (alerts || []).map((a) => {
        if (a.id !== id) return a;
        if (echoed) return { ...a, ...echoed };
        return { ...a, repVerdict: verdict, repNote: note || null, repVerdictAt: now || new Date().toISOString() };
    });
};

/** One alert: time · severity · summary · keyword, the chat deep link, and
 *  the two feedback chips. The recorded verdict is shown filled; the other
 *  chip stays clickable to change it. ✗ takes an optional short note (the
 *  KnowledgeChip flag-input pattern). */
function AlertRow({ alert, on_feedback }) {
    const [mode, set_mode] = useState('idle'); // idle | noting | busy
    const [note, set_note] = useState('');
    const href = alert_chat_href(alert);
    const recorded = alert.repVerdict || null; // GENUINE | NOT_ESCALATION | null
    const submit = async (verdict) => {
        if (mode === 'busy') return;
        set_mode('busy');
        const body = { verdict, ...(verdict === 'NOT_ESCALATION' && note.trim() ? { note: note.trim() } : {}) };
        try {
            const r = await cs_analysis_fetch({ path: `/api/v1/alerts/${encodeURIComponent(alert.id)}/feedback`, method: 'POST', body });
            on_feedback(alert.id, verdict, body.note || null, r);
            set_note('');
            set_mode('idle');
        } catch (e) { set_mode('idle'); window.alert('Could not record that — please try again.'); }
    };
    const chip = (verdict, label, color) => {
        const active = recorded === verdict;
        return (
            <span key={verdict} title={active ? 'Recorded — click the other chip to change it' : verdict === 'GENUINE' ? 'This was a real escalation' : 'False alarm — not an escalation'}
                onClick={() => { if (active || mode === 'busy') return; if (verdict === 'GENUINE') submit('GENUINE'); else set_mode('noting'); }}
                style={{ fontSize: 10.5, whiteSpace: 'nowrap', borderRadius: 10, padding: '1px 8px', cursor: active ? 'default' : 'pointer', fontWeight: active ? 700 : 500,
                    border: `1px solid ${color}`, color: active ? '#fff' : color, background: active ? color : '#fff' }}>
                {label}
            </span>
        );
    };
    return (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '5px 0', borderBottom: '1px solid #f0f2f4', fontSize: 12, opacity: is_suppressed(alert) ? 0.7 : 1 }}>
            <span style={{ flex: '0 0 96px', color: '#95a5a6', fontSize: 11, whiteSpace: 'nowrap', paddingTop: 1 }}>{fmt_ts(alert.createdAt)}</span>
            {alert.severity ? (
                <span style={{ flex: 'none', fontSize: 10, fontWeight: 700, color: '#fff', background: SEVERITY_COLOR[alert.severity] || '#95a5a6', borderRadius: 8, padding: '1px 6px', lineHeight: '15px' }}>{alert.severity}</span>
            ) : null}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: '#33414e' }}>{alert.summary ? alert.summary : <i style={{ color: '#95a5a6' }}>(no summary)</i>}</div>
                <div style={{ fontSize: 10.5, color: '#95a5a6', marginTop: 1 }}>
                    {alert.keyword ? <span>keyword “{alert.keyword}”</span> : <span>no keyword</span>}
                    {alert.category ? ` · ${alert.category}` : ''}
                    {alert.verdict !== 'FIRED' ? ` · ${verdict_label(alert.verdict)}` : ''}
                    {alert.verdict === 'FIRED' ? ` · ${alert.postedToSpace ? 'posted to space' : 'not posted'}${alert.dmDelivered ? ' · rep DM' : ''}` : ''}
                    {href ? <> · <Link to={href} style={{ color: '#2f6fed' }}>open in chat →</Link></> : null}
                </div>
                {recorded ? (
                    <div title={alert.repVerdictBy ? `by ${alert.repVerdictBy}` : undefined} style={{ fontSize: 10.5, marginTop: 2, color: recorded === 'GENUINE' ? '#2e9e5b' : '#c0392b' }}>
                        {recorded === 'GENUINE' ? '✓ genuine' : '✗ not an escalation'}{alert.repVerdictAt ? ` · ${fmt_ts(alert.repVerdictAt)}` : ''}
                        {alert.repNote ? <span style={{ color: '#7f8c8d' }}> — {cut_text(alert.repNote, 120)}</span> : null}
                    </div>
                ) : null}
                {mode === 'noting' ? (
                    <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                        <Input bsSize='sm' value={note} placeholder='Why is it not an escalation? (optional)' autoFocus
                            onChange={(e) => set_note(e.target.value)}
                            onKeyDown={(e) => {
                                e.stopPropagation();
                                if (e.key === 'Enter') { e.preventDefault(); submit('NOT_ESCALATION'); }
                                else if (e.key === 'Escape') { e.preventDefault(); set_mode('idle'); }
                            }}
                            style={{ fontSize: 11, padding: '2px 6px', height: 24 }} />
                        <Button size='sm' color='danger' outline style={{ fontSize: 11, padding: '0 8px' }} onClick={() => submit('NOT_ESCALATION')}>Record</Button>
                        <Button size='sm' color='light' style={{ fontSize: 11, padding: '0 8px' }} onClick={() => set_mode('idle')}>Cancel</Button>
                    </div>
                ) : null}
            </div>
            <div style={{ flex: 'none', display: 'flex', gap: 4, alignItems: 'center', paddingTop: 1 }}>
                {mode === 'busy' ? <Spinner size='sm' style={{ width: 12, height: 12 }} /> : (
                    <>
                        {chip('GENUINE', '✓ Genuine', '#2e9e5b')}
                        {chip('NOT_ESCALATION', '✗ Not an escalation', '#c0392b')}
                    </>
                )}
            </div>
        </div>
    );
}

export default function EscalationAlerts({ store_id }) {
    const [alerts, set_alerts] = useState(null); // null = loading
    const [error, set_error] = useState(null);
    const [show_suppressed, set_show_suppressed] = useState(false);
    const [reload_n, set_reload_n] = useState(0);
    const [open, set_open] = useState(null); // null = auto: expanded only when there are alerts
    useEffect(() => { set_alerts(null); set_open(null); set_show_suppressed(false); }, [store_id]);
    useEffect(() => {
        if (!store_id) return undefined;
        let alive = true;
        set_error(null);
        cs_analysis_fetch({ path: '/api/v1/alerts', query: { clientId: store_id, limit: 20, includeSuppressed: show_suppressed ? 1 : 0 } })
            .then((r) => { if (alive) set_alerts(r && Array.isArray(r.alerts) ? r.alerts : []); })
            .catch((e) => { if (alive) { set_alerts([]); set_error((e && e.message) || 'could not load alerts'); } });
        return () => { alive = false; };
    }, [store_id, show_suppressed, reload_n]);
    const on_feedback = (id, verdict, note, resp) => set_alerts((cur) => apply_feedback(cur, id, verdict, note, resp));

    const list = alerts || [];
    const live_n = list.filter((a) => !is_suppressed(a)).length;
    const expanded = open !== null ? open : live_n > 0;
    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#2f6fed' }} onClick={() => set_open(!expanded)}>
                    {expanded ? '▾' : '▸'} Escalation alerts{alerts === null ? '' : ` (${live_n})`}
                </div>
                {alerts === null ? <Spinner size='sm' style={{ width: 12, height: 12 }} /> : null}
                {expanded ? (
                    <>
                        <label style={{ fontSize: 11, color: '#7f8c8d', marginLeft: 'auto', marginBottom: 0, whiteSpace: 'nowrap', cursor: 'pointer' }}>
                            <input type='checkbox' checked={show_suppressed} onChange={(e) => set_show_suppressed(e.target.checked)} style={{ marginRight: 4 }} />
                            show suppressed
                        </label>
                        <span onClick={() => set_reload_n((n) => n + 1)} title='Refresh' style={{ cursor: 'pointer', color: '#95a5a6', fontSize: 13, lineHeight: 1 }}>↻</span>
                    </>
                ) : null}
            </div>
            {expanded ? (
                <div style={{ marginTop: 4 }}>
                    {error ? <div style={{ fontSize: 11, color: '#c0392b' }}>Could not load alerts — {cut_text(error, 160)}</div> : null}
                    {alerts !== null && !error && list.length === 0 ? (
                        <div style={{ fontSize: 12, color: '#95a5a6' }}>No escalation alerts for this client{show_suppressed ? '' : ' — tick "show suppressed" to include hits the judge dropped'}.</div>
                    ) : null}
                    {list.map((a) => <AlertRow key={a.id} alert={a} on_feedback={on_feedback} />)}
                    {list.length ? (
                        <div style={{ fontSize: 10.5, color: '#b0b8bd', marginTop: 4 }}>
                            ✓ / ✗ label each alert for the judge — genuine escalations and false alarms both tune it. Missed one? Use ⚠ on the client's message in the chat.
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}
